(() => {
  const initHeaderCustomMegaMenu = () => {
    if (customElements.get('mega-menu-swiper')) return;

    class MegaMenuSwiper extends HTMLElement {
      constructor() {
        super();
        this.onDetailsToggle = this.onDetailsToggle.bind(this);
        this.onScriptReady = this.onScriptReady.bind(this);
        this.onStampedBadgeMutation = this.onStampedBadgeMutation.bind(this);
        this.onStampedBadgeInteraction = this.onStampedBadgeInteraction.bind(this);
        this.openStampedBadgeProductUrl = this.openStampedBadgeProductUrl.bind(this);
        this.syncStampedBadgeLinks = this.syncStampedBadgeLinks.bind(this);
        this.onIntersection = this.onIntersection.bind(this);
        this.onSliderPointerDown = this.onSliderPointerDown.bind(this);
        this.onSliderClick = this.onSliderClick.bind(this);
        this.onNavPointerDown = this.onNavPointerDown.bind(this);
        this.onNavClick = this.onNavClick.bind(this);
        this.swiper = null;
        this.intersectionObserver = null;
        this.stampedBadgeObservers = [];
        this.stampedBadgeFormatTimeout = null;
        this.isInViewport = false;
      }

      connectedCallback() {
        this.slider = this.querySelector('[data-mega-menu-swiper]');
        this.prevButton = this.querySelector('[data-mega-menu-prev]');
        this.nextButton = this.querySelector('[data-mega-menu-next]');
        this.details = this.closest('details');

        if (!this.slider) return;

        this.details?.addEventListener('toggle', this.onDetailsToggle);
        window.addEventListener('third-party-scripts-loaded', this.onScriptReady);
        window.addEventListener('load', this.onScriptReady);

        this.slider.addEventListener('pointerdown', this.onSliderPointerDown);
        this.slider.addEventListener('click', this.onSliderClick);

        if (this.prevButton) {
          this.prevButton.addEventListener('pointerdown', this.onNavPointerDown);
          this.prevButton.addEventListener('click', this.onNavClick);
        }

        if (this.nextButton) {
          this.nextButton.addEventListener('pointerdown', this.onNavPointerDown);
          this.nextButton.addEventListener('click', this.onNavClick);
        }

        this.setupIntersectionObserver();
        this.setupStampedBadgeObservers();
        this.formatStampedBadgeReviewCounts();
        this.syncStampedBadgeLinks();
        this.stampedBadgeFormatTimeout = window.setTimeout(() => {
          this.formatStampedBadgeReviewCounts();
          this.syncStampedBadgeLinks();
        }, 800);
        this.onScriptReady();
      }

      disconnectedCallback() {
        this.details?.removeEventListener('toggle', this.onDetailsToggle);
        window.removeEventListener('third-party-scripts-loaded', this.onScriptReady);
        window.removeEventListener('load', this.onScriptReady);

        this.slider?.removeEventListener('pointerdown', this.onSliderPointerDown);
        this.slider?.removeEventListener('click', this.onSliderClick);
        this.prevButton?.removeEventListener('pointerdown', this.onNavPointerDown);
        this.prevButton?.removeEventListener('click', this.onNavClick);
        this.nextButton?.removeEventListener('pointerdown', this.onNavPointerDown);
        this.nextButton?.removeEventListener('click', this.onNavClick);

        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
          this.intersectionObserver = null;
        }

        if (this.stampedBadgeFormatTimeout) {
          window.clearTimeout(this.stampedBadgeFormatTimeout);
          this.stampedBadgeFormatTimeout = null;
        }

        this.querySelectorAll('.mega-menu__real-stars-badge-wrapper').forEach((wrapper) => {
          wrapper.removeEventListener('click', this.onStampedBadgeInteraction, true);
          wrapper.removeEventListener('auxclick', this.onStampedBadgeInteraction, true);
          delete wrapper.dataset.stampedInteractionAttached;
        });

        this.stampedBadgeObservers.forEach((observer) => observer.disconnect());
        this.stampedBadgeObservers = [];

        if (this.swiper && typeof this.swiper.destroy === 'function') {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }

      setupIntersectionObserver() {
        if (!('IntersectionObserver' in window)) {
          this.isInViewport = true;
          return;
        }

        this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
          root: null,
          rootMargin: '0px',
          threshold: 0.1,
        });

        this.intersectionObserver.observe(this);
      }

      setupStampedBadgeObservers() {
        const badgeWrappers = this.querySelectorAll('.mega-menu__real-stars-badge-wrapper');

        badgeWrappers.forEach((wrapper) => {
          const observer = new MutationObserver(this.onStampedBadgeMutation);
          observer.observe(wrapper, { childList: true, subtree: true, characterData: true });
          this.stampedBadgeObservers.push(observer);

          if (wrapper.dataset.stampedInteractionAttached === 'true') return;

          wrapper.addEventListener('click', this.onStampedBadgeInteraction, true);
          wrapper.addEventListener('auxclick', this.onStampedBadgeInteraction, true);
          wrapper.dataset.stampedInteractionAttached = 'true';
        });
      }

      onStampedBadgeInteraction(event) {
        const badge = event.target.closest('.stamped-product-reviews-badge');
        if (!badge) return;

        const wrapper = badge.closest('.mega-menu__real-stars-badge-wrapper');
        const productUrl = wrapper?.getAttribute('href');
        if (!productUrl) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();

        if (event.type === 'auxclick' && event.button !== 1) return;
        if (event.type === 'click' && typeof event.button === 'number' && event.button !== 0) return;

        if (event.type === 'click' || event.type === 'auxclick') {
          this.openStampedBadgeProductUrl(productUrl, event);
        }
      }

      openStampedBadgeProductUrl(productUrl, event) {
        const shouldOpenInNewTab = event.metaKey || event.ctrlKey || event.shiftKey || (event.type === 'auxclick' && event.button === 1);

        if (shouldOpenInNewTab) {
          window.open(productUrl, '_blank', 'noopener');
          return;
        }

        window.location.assign(productUrl);
      }

      onStampedBadgeMutation() {
        this.formatStampedBadgeReviewCounts();
        this.syncStampedBadgeLinks();
      }

      formatStampedBadgeReviewCounts() {
        const reviewCaptions = this.querySelectorAll(
          '.mega-menu__real-stars-badge-wrapper .stamped-product-reviews-badge .stamped-badge-caption[data-reviews]'
        );

        reviewCaptions.forEach((caption) => {
          const reviewsCount = caption.dataset.reviews?.trim();
          if (!reviewsCount || !/\d/.test(reviewsCount)) return;

          const formattedReviewsCount = `(${reviewsCount})`;
          const normalizedCaptionText = caption.textContent?.replace(/\s+/g, '').trim();

          if (normalizedCaptionText === formattedReviewsCount || /^\([\d,.]+\)$/.test(normalizedCaptionText || '')) return;

          caption.textContent = formattedReviewsCount;
        });
      }

      syncStampedBadgeLinks() {
        const badgeLinks = this.querySelectorAll(
          '.mega-menu__real-stars-badge-wrapper .stamped-product-reviews-badge a[href]'
        );

        badgeLinks.forEach((badgeLink) => {
          const wrapper = badgeLink.closest('.mega-menu__real-stars-badge-wrapper');
          const productUrl = wrapper?.getAttribute('href');
          if (!productUrl) return;

          if (badgeLink.getAttribute('href') !== productUrl) {
            badgeLink.setAttribute('href', productUrl);
          }

          badgeLink.setAttribute('tabindex', '-1');
        });
      }

      onIntersection(entries) {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.isInViewport = true;
          this.tryInit();
          this.intersectionObserver?.unobserve(this);
        });
      }

      onDetailsToggle() {
        if (!this.details?.open) return;
        this.isInViewport = true;
        this.tryInit();
        if (this.swiper) {
          this.swiper.update();
        }
      }

      onScriptReady() {
        this.hasSwiperLibrary = typeof window.Swiper === 'function';
        this.tryInit();
        this.formatStampedBadgeReviewCounts();
        this.syncStampedBadgeLinks();
      }

      tryInit() {
        if (this.swiper || !this.hasSwiperLibrary || !this.isInViewport) return;
        this.initSwiper();
      }

      onSliderPointerDown(event) {
        event.stopPropagation();
      }

      onSliderClick(event) {
        event.stopPropagation();
      }

      onNavPointerDown(event) {
        event.preventDefault();
        event.stopPropagation();
      }

      onNavClick(event) {
        event.stopPropagation();
      }

      initSwiper() {
        const swiperConfig = {
          slidesPerView: 'auto',
          spaceBetween: 16,
          speed: 400,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          centeredSlides: false,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 16,
          breakpoints: {
            990: {
              slidesPerView: 3.25,
              spaceBetween: 16,
            },
          },
        };

        if (this.prevButton && this.nextButton) {
          swiperConfig.navigation = {
            prevEl: this.prevButton,
            nextEl: this.nextButton,
          };
        }

        this.swiper = new window.Swiper(this.slider, swiperConfig);
      }
    }

    customElements.define('mega-menu-swiper', MegaMenuSwiper);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderCustomMegaMenu, { once: true });
    return;
  }

  initHeaderCustomMegaMenu();

  function updateMegaMenuActiveState() {
    const currentMatch = window.location.pathname + window.location.search;

    document.querySelectorAll('.mega-menu--custom').forEach((megaMenu) => {
      megaMenu.querySelectorAll('.mega-menu__column-link--active').forEach((link) => {
        link.classList.remove('mega-menu__column-link--active');
        link.removeAttribute('aria-current');
      });

      const summarySpan = megaMenu.querySelector('summary > span');
      if (summarySpan) {
        summarySpan.classList.remove('header__active-menu-item');
      }

      let hasActiveChild = false;

      megaMenu.querySelectorAll('.mega-menu__column-link').forEach((link) => {
        const linkUrl = new URL(link.href, window.location.origin);
        const linkMatch = linkUrl.pathname + linkUrl.search;

        if (linkMatch === currentMatch) {
          link.classList.add('mega-menu__column-link--active');
          link.setAttribute('aria-current', 'page');
          hasActiveChild = true;
        }
      });

      if (hasActiveChild && summarySpan) {
        summarySpan.classList.add('header__active-menu-item');
      }
    });
  }

  updateMegaMenuActiveState();

  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function () {
    originalPushState.apply(this, arguments);
    updateMegaMenuActiveState();
  };

  history.replaceState = function () {
    originalReplaceState.apply(this, arguments);
    updateMegaMenuActiveState();
  };

  window.addEventListener('popstate', updateMegaMenuActiveState);
})();
